import { useState, useEffect, useRef, FormEvent } from 'react';
import { useIntl } from 'react-intl';
import { ContentLayout, FormField, Input } from '@amzn/awsui-components-react';

import TopHeader from '../components/TopHeader';
import Main from '../components/Main';
import Footer from '../components/Footer';
import GandalfFlashbar from '../components/GandalfFlashbar';
import usePageTitle from '../hooks/usePageTitle';
import { useSignInPageSetup } from '../hooks/useSignInPageSetup';
import useLoginMethod from '../hooks/useLoginMethod';
import metricsService from '../services/metrics';
import { IMPRESSIONS } from '../constants/metrics';
import { AppConfig } from '../types/app';
import messages from '../i18n/simplifiedSignIn.messages';
import styles from './SimplifiedSignIn.module.css';
import googleLogo from '../assets/google-logo.svg';
import appleLogo from '../assets/apple-logo.svg';
import githubLogo from '../assets/github-logo.svg';
import amazonLogo from '../assets/lwa.svg';
import keyIcon from '../assets/key.svg';
import infoIcon from '../assets/status-info.svg';
import { validateEmail } from '../utils/email';
import Cookies from 'js-cookie';
import { VERIFY_EMAIL_COOKIE_NAME } from '../components/postAuth/VerifyEmail';
import providersService from '../services/providers';
import { rumService } from '../services/rum';
import { AppURL } from '../constants/urls';

function SimplifiedSignIn({ config }: { config: AppConfig }) {
  usePageTitle();
  const { formatMessage } = useIntl();
  const { enableCookieComponent, handleRedirectToIdp } =
    useSignInPageSetup(config);
  const metricsPublisher = useRef(
    metricsService.getPublisher('SimplifiedSignInPage')
  );
  const {
    isLoading,
    error: loginMethodError,
    loginMethods,
    fetchLoginMethod,
  } = useLoginMethod(config);

  const [email, setEmail] = useState('');
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [helpExpanded, setHelpExpanded] = useState(false);

  const enableGoogle = config.enableGoogleSignIn !== 'false';
  const enableApple = config.enableAppleSignIn !== 'false';
  const enableGithub = config.enableGithubSignIn !== 'false';
  const enableAmazon = config.enableAmazonSignIn !== 'false';
  const hasSocialProviders =
    enableGoogle || enableApple || enableGithub || enableAmazon;

  // When loginMethod API returns exactly 1 IDP, redirect to it (SSO flow)
  useEffect(() => {
    if (loginMethods?.length === 1) {
      const idpName = loginMethods[0];
      const provider = providersService.transformNameToIdProvider(
        idpName,
        config.gandalfDomain
      );
      metricsPublisher.current.publishCounterMonitor('SSORedirect', 1);
      rumService.recordEvent('SimplifiedSignInSSORedirect', {
        provider: idpName,
        url: window.location.href,
        userAgent: navigator.userAgent,
        path: `${window.location.pathname}${window.location.search}`,
      });
      handleRedirectToIdp(provider);
    }
  }, [loginMethods, config.gandalfDomain, handleRedirectToIdp]);

  // Capture metric when loginMethod API returns an error
  useEffect(() => {
    if (loginMethodError) {
      metricsPublisher.current.publishCounterMonitor('Error', 1);
      metricsPublisher.current.publishStringTruncate(
        'ErrorMsg',
        loginMethodError.toString()
      );
      metricsPublisher.current.publishStringTruncate(
        'UserAgent',
        navigator.userAgent
      );
      rumService.recordEvent('SimplifiedSignInError', {
        error: loginMethodError.message,
        url: window.location.href,
        userAgent: navigator.userAgent,
        path: `${window.location.pathname}${window.location.search}`,
      });
    }
  }, [loginMethodError]);

  useEffect(() => {
    metricsPublisher.current.publishCounterMonitor(IMPRESSIONS, 1);
    Cookies.remove(VERIFY_EMAIL_COOKIE_NAME);
    const handlePageShow = (event: PageTransitionEvent) => {
      const isBFEventOld =
        window.performance &&
        window.performance.navigation.type ===
          window.performance.navigation.TYPE_BACK_FORWARD;
      const navEntry = performance.getEntriesByType('navigation')[0] as
        | PerformanceNavigationTiming
        | undefined;
      const isBFEventNew = navEntry?.type === 'back_forward';
      if (isBFEventOld || isBFEventNew || event.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const handleContinue = (e: FormEvent) => {
    e.preventDefault();
    const isValid = validateEmail(email);
    setIsValidEmail(isValid);
    if (!isValid) return;
    fetchLoginMethod(email);
  };

  const handleSocialSignIn = (provider: string) => {
    // Placeholder for social sign-in
    const params = new URLSearchParams(window.location.search);
    params.set('provider', provider);
    window.location.href = `/login?${params.toString()}`;
  };

  return (
    <>
      <TopHeader config={config} />
      <Main config={config}>
        <ContentLayout defaultPadding maxContentWidth={1040}>
          <GandalfFlashbar />
          <div className={styles.container}>
            <div className={styles.card}>
              <h1 className={styles.header}>
                {formatMessage(messages.header)}
              </h1>

              <form onSubmit={handleContinue} noValidate>
                <FormField
                  errorText={
                    !isValidEmail
                      ? formatMessage(messages.emailInvalid)
                      : loginMethodError?.message || ''
                  }
                >
                  <span className={styles.inputLabel}>
                    {formatMessage(messages.emailLabel)}
                  </span>
                  <Input
                    type="email"
                    value={email}
                    ariaLabel={formatMessage(messages.emailLabel)}
                    placeholder={formatMessage(messages.emailPlaceholder)}
                    onChange={({ detail }) => {
                      setEmail(detail.value);
                      setIsValidEmail(true);
                    }}
                    data-testid="simplified-email-input"
                  />
                </FormField>

                <button
                  type="submit"
                  className={styles.primaryButton}
                  data-testid="simplified-continue-button"
                  disabled={isLoading}
                >
                  {formatMessage(messages.continueButton)}
                </button>
              </form>

              {/* Separator */}
              {hasSocialProviders && (
                <div className={styles.separator}>
                  <div className={styles.separatorLine} />
                  <span className={styles.separatorText}>
                    {formatMessage(messages.orContinueWith)}
                  </span>
                  <div className={styles.separatorLine} />
                </div>
              )}

              {/* Social sign-in tiles */}
              {hasSocialProviders && (
                <div className={styles.socialTiles}>
                  {enableGoogle && (
                    <button
                      type="button"
                      className={styles.socialTile}
                      onClick={() => handleSocialSignIn('Google')}
                      data-testid="simplified-google-button"
                    >
                      <img
                        src={googleLogo}
                        alt=""
                        className={styles.socialIcon}
                      />
                      <span className={styles.socialLabel}>
                        {formatMessage(messages.googleLabel)}
                      </span>
                    </button>
                  )}
                  {enableApple && (
                    <button
                      type="button"
                      className={styles.socialTile}
                      onClick={() => handleSocialSignIn('Apple')}
                      data-testid="simplified-apple-button"
                    >
                      <img
                        src={appleLogo}
                        alt=""
                        className={styles.socialIcon}
                      />
                      <span className={styles.socialLabel}>
                        {formatMessage(messages.appleLabel)}
                      </span>
                    </button>
                  )}
                  {enableGithub && (
                    <button
                      type="button"
                      className={styles.socialTile}
                      onClick={() => handleSocialSignIn('Github')}
                      data-testid="simplified-github-button"
                    >
                      <img
                        src={githubLogo}
                        alt=""
                        className={styles.socialIcon}
                      />
                      <span className={styles.socialLabel}>
                        {formatMessage(messages.githubLabel)}
                      </span>
                    </button>
                  )}
                  {enableAmazon && (
                    <button
                      type="button"
                      className={styles.socialTile}
                      onClick={() => handleSocialSignIn('Amazon')}
                      data-testid="simplified-amazon-button"
                    >
                      <img
                        src={amazonLogo}
                        alt=""
                        className={styles.socialIcon}
                      />
                      <span className={styles.socialLabel}>
                        {formatMessage(messages.amazonLabel)}
                      </span>
                    </button>
                  )}
                </div>
              )}

              {/* Legal text */}
              <p className={styles.legalText}>
                {formatMessage(messages.legalText, {
                  customerAgreement: (
                    <a
                      href="https://aws.amazon.com/agreement/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {formatMessage(messages.customerAgreement)}
                    </a>
                  ),
                  serviceTerms: (
                    <a
                      href="https://aws.amazon.com/service-terms/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {formatMessage(messages.serviceTerms)}
                    </a>
                  ),
                  privacyNotice: (
                    <a
                      href="https://aws.amazon.com/privacy/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {formatMessage(messages.privacyNotice)}
                    </a>
                  ),
                })}
              </p>

              {/* Help section */}
              {helpExpanded && (
                <div className={styles.helpSection}>
                  <div className={styles.helpDivider} />
                  <p className={styles.helpToggleLabel}>
                    {formatMessage(messages.getHelpSigningIn)}:
                  </p>
                  <div className={styles.helpTiles}>
                    <button
                      type="button"
                      className={styles.helpTile}
                      onClick={() => {
                        metricsPublisher.current.publishCounterMonitor(
                          'SwitchToOldUI',
                          1
                        );
                        rumService.recordEvent(
                          'SimplifiedSignInSwitchToOldUI',
                          {
                            url: window.location.href,
                            userAgent: navigator.userAgent,
                            path: `${window.location.pathname}${window.location.search}`,
                          }
                        );
                        const params = new URLSearchParams(
                          window.location.search
                        );
                        window.location.href = `${
                          AppURL.LoginClassic
                        }?${params.toString()}`;
                      }}
                      data-testid="switch-old-ui"
                    >
                      <img
                        src={keyIcon}
                        alt=""
                        className={styles.helpTileIcon}
                      />
                      <span className={styles.helpTileLabel}>
                        {formatMessage(messages.switchToOldUi)}
                      </span>
                    </button>
                    <button
                      type="button"
                      className={styles.helpTile}
                      onClick={() => {
                        window.location.href = '/help';
                      }}
                      data-testid="faq-link"
                    >
                      <img
                        src={infoIcon}
                        alt=""
                        className={styles.helpTileIcon}
                      />
                      <span className={styles.helpTileLabel}>
                        {formatMessage(messages.faq)}
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Help link - right aligned, hides when expanded */}
            {!helpExpanded && (
              <div className={styles.helpLink}>
                <button
                  type="button"
                  onClick={() => setHelpExpanded(true)}
                  data-testid="help-toggle"
                >
                  {formatMessage(messages.getHelpSigningIn)}
                </button>
              </div>
            )}
          </div>
        </ContentLayout>
      </Main>
      <Footer enableCookieComponent={enableCookieComponent} />
    </>
  );
}

export default SimplifiedSignIn;
